/**
 * MessageInput Component
 * Text input and send button at bottom of sheet
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart - shareBottomActions
 *
 * SELF-OWNED draft text (uncontrolled). The component owns its own
 * composer state and emits the current text via `onSend(text)`. This avoids
 * round-tripping every keystroke through the parent — historically a
 * `MockChatPage(reversed VirtualList)` re-render per character (see
 * `useReversedAnchor` diagnostic logs). Callers that need to clear the input
 * after a successful send hold a ref of type `MessageInputHandle` and call
 * `.clear()`. Callers that should preserve the draft on failure (e.g.
 * `SearchUsersContent` when the share use case rejects) simply do not call
 * `.clear()`.
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { AddMediaButton } from '@/presentation/components/common/controls/AddMediaButton';
import { GoLiveButton } from '@/presentation/components/common/controls/GoLiveButton';
import { InputText, type InputTextElement } from '@/presentation/components/common/InputText';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

export interface MessageInputProps {
  /** Optional initial draft text. Defaults to `''`. Not re-read after mount. */
  defaultValue?: string;
  /**
   * Called with the current draft when the user presses Enter (without Shift)
   * or clicks the send button. The draft is NOT cleared automatically — the
   * caller decides via the imperative `clear()` handle (see component docs).
   */
  onSend: (text: string) => void;
  disabled: boolean;
  loading: boolean;
  placeholder?: string;
  /**
   * Optional `data-qa-*` set forwarded to the underlying `InputText` (and onto
   * its rendered element) so the qa-tester can locate the composer field.
   */
  qa?: QaAttrOptions;
  /**
   * Optional `data-qa-*` set for the send `<button>`. `MessageInput` renders two
   * interactive nodes (composer input + send button), so the send affordance
   * takes its own options object.
   */
  sendQa?: QaAttrOptions;
  /**
   * Optional background color for the underlying InputText field. Forwarded to
   * `InputText`; when omitted, `InputText` falls back to `theme.modalBackground`
   * so existing callers are unaffected.
   */
  backgroundColor?: string;
  /**
   * Optional add-media tap handler. When provided, the common `AddMediaButton`
   * (the web port of Flutter's `AddMediaButtonWidget`) is rendered between the
   * input and the send button, styled like the chat composer usage
   * (`ChatMessageInput.tsx:190-197`). When omitted, nothing extra renders, so
   * all other `MessageInput` callers are unaffected. Used by `CommunityComposer`
   * to open the add-content sheet (`community_screen.dart:1331-1344`).
   */
  onMediaTap?: () => void;
  /**
   * Optional `data-qa-*` set for the add-media `<div>`. Only meaningful together
   * with `onMediaTap`; `MessageInput` renders a third interactive node when the
   * add-media affordance is enabled, so it takes its own options object.
   */
  mediaQa?: QaAttrOptions;
  /**
   * Optional go-live tap handler. When provided, the common `GoLiveButton` (the
   * web port of Flutter's `GoLiveButtonWidget`) is rendered between the add-media
   * button and the send button — the slot Flutter uses
   * (`community_screen.dart:1351-1368`). When omitted, nothing extra renders, so
   * all other `MessageInput` callers are unaffected. Used by `CommunityComposer`
   * (only when the current user owns the wall) to open the broadcaster go-live
   * flow.
   */
  onGoLive?: () => void;
  /**
   * Optional. When true, the go-live button renders in a dimmed, non-interactive
   * state (visible but inert) instead of firing `onGoLive`. Only meaningful
   * together with `onGoLive`; defaults to `false`, so all other callers are
   * unaffected. Used by `CommunityComposer` to disable the wall owner's go-live
   * while they are ALREADY broadcasting a LIVE session (note-2 guard) — the
   * ledger rule is to disable + distinctly mark the control, not hide it.
   */
  goLiveDisabled?: boolean;
  /**
   * Optional `data-qa-*` set for the go-live `<div>`. Only meaningful together
   * with `onGoLive`.
   */
  goLiveQa?: QaAttrOptions;
}

export interface MessageInputHandle {
  /** Reset the draft to the empty string and collapse the auto-resized textarea. */
  clear: () => void;
  /**
   * Move keyboard focus to the composer field. Additive (existing `clear()`
   * callers are unaffected); used by `CommunityComposer` to focus the input
   * when a reply target is set, mirroring Flutter `_focusNode.requestFocus()`
   * (`community_screen.dart:744`).
   */
  focus: () => void;
  /**
   * Return the current draft text. Additive (existing `clear()` / `focus()`
   * callers are unaffected); used by `CommunityComposer` to snapshot the draft
   * at the moment the add-media button is tapped (Flutter captures the composer
   * text before navigating, `community_screen.dart:443`).
   */
  getValue: () => string;
}

export const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  function MessageInput(
    {
      defaultValue = '',
      onSend,
      disabled,
      loading,
      placeholder,
      qa,
      sendQa,
      backgroundColor,
      onMediaTap,
      mediaQa,
      onGoLive,
      goLiveDisabled = false,
      goLiveQa,
    },
    ref,
  ) {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const [value, setValue] = useState<string>(defaultValue);
    const [isInputingText, setIsInputingText] = useState<boolean>(
      defaultValue.length > 0,
    );
    const inputRef = useRef<InputTextElement | null>(null);
    // Live mirror of the draft so the imperative `getValue()` handle can read the
    // current text without recreating the handle (deps stay `[]`) on every keystroke.
    const valueRef = useRef<string>(defaultValue);

    const resetTextareaHeight = () => {
      if (inputRef.current) inputRef.current.style.height = 'auto';
    };

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          setValue('');
          valueRef.current = '';
          setIsInputingText(false);
          resetTextareaHeight();
        },
        focus: () => {
          inputRef.current?.focus();
        },
        getValue: () => valueRef.current,
      }),
      [],
    );

    const handleTextChanged = (newValue: string) => {
      setValue(newValue);
      valueRef.current = newValue;

      // Matches Flutter logic from _textChanged (line 311)
      if (!isInputingText && newValue.length > 0) {
        setIsInputingText(true);
      } else if (newValue.length === 0) {
        setIsInputingText(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<InputTextElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !disabled && !loading) {
        e.preventDefault();
        handleSend();
      }
    };

    const handleSend = () => {
      onSend(value);
      resetTextareaHeight();
    };

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
          paddingTop: responsive.spacing.xl,
          paddingBottom: responsive.spacing.xl,
          borderTop: `${responsive.border.thin}px solid ${theme.border}`,
          gap: responsive.gap.sm,
        }}
      >
        <InputText
          inputRef={inputRef}
          value={value}
          onChange={handleTextChanged}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          multiline
          autoResize
          lineHeight={1.4}
          maxHeight="6.2em"
          backgroundColor={backgroundColor}
          style={{ flex: 1 }}
          qa={qa}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.xl,
            flexShrink: 0,
          }}
        >

          {/* Add-media button — rendered only when the caller supplies
            `onMediaTap` (e.g. `CommunityComposer`). Styled like the chat
            composer usage (`ChatMessageInput.tsx:190-197`). Omitted callers are
            unaffected. Web deviation: always mounted (Flutter focus-gates it,
            `community_screen.dart:1332`) — the same deviation the chat composer
            documents. */}
          {onMediaTap && (
            <AddMediaButton
              backgroundColor={theme.background}
              iconBackgroundColor={theme.secondary}
              buttonSize={responsive.button.size.xs}
              iconSize={responsive.icon.small}
              onTap={onMediaTap}
              qa={mediaQa}
            />
          )}

          {/* Go-live button — rendered only when the caller supplies `onGoLive`
            (e.g. `CommunityComposer`, gated on wall ownership). Placed between the
            add-media button and the send button, matching Flutter's action-row
            order (`community_screen.dart:1351-1368`). Omitted callers are
            unaffected. */}
          {onGoLive && (
            <GoLiveButton
              backgroundColor={theme.background}
              iconBackgroundColor={theme.secondary}
              buttonSize={responsive.button.size.xs}
              dotSize={responsive.icon.xxs}
              onTap={onGoLive}
              disabled={goLiveDisabled}
              qa={goLiveQa}
            />
          )}

          <button
            {...(sendQa ? qaAttr(sendQa) : {})}
            onClick={handleSend}
            disabled={disabled || loading}
            style={{
              borderRadius: responsive.borderRadius.round,
              border: 'none',
              cursor: disabled || loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: isInputingText ? 'rotate(-45deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), background-color 0.2s',
            }}
          >
            {loading ? (
              <div style={{ transform: isInputingText ? 'rotate(45deg)' : 'rotate(0deg)' }}>
                <ExpauseLoadingIndicator />
              </div>
            ) : (
              <AssetIcon
                path="/assets/images/send.png"
                width={responsive.button.size.xxs}
                height={responsive.button.size.xxs}
                color={disabled ? theme.disabled : isInputingText ? theme.exclusive : theme.icon.primary}
                transition="background-color 0.2s"
              />
            )}
          </button>
        </div>
      </div>
    );
  },
);
